import React, { useEffect, useState } from 'react';
import { UploadCloud, Database, Archive } from 'lucide-react';
import FilterableMultiSelect from '../../common/FilterableMultiSelect';
import { authservice } from '../../../../components/api/authservice';
import { knowledgebaseService } from '../../../../components/api/knowledgebaseService';
import { apiService } from '../../../../components/api/apiService';
import LoaderOverlay from '../../Dialogs/LoaderOverlay';
import { ingestionService } from '../../../../components/api/ingestionService';
import { dataModelsCatalogService, type DataModelCatalogRow } from '../../MCP/dataModelsCatalogService';
const chunkingOptions = ['By Paragraph', 'By Sentence', 'Custom Tokens', 'Line'];
const chunkingModeToApi = (display: string) =>
  display ? display.replace(/\s/g, '') : '';

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  tenantId: string;
  userId: string;
  workspaceid: string;
  isEditMode?: boolean;
  existingKbId?: string;
}

const UploadToKnowledgebaseModal: React.FC<Props> = ({
  onClose,
  onSubmit,
  tenantId,
  userId,
  workspaceid,
  isEditMode = false,
  existingKbId
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [chunkingMode, setChunkingMode] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [destinationType, setDestinationType] = useState<'vector' | 'vault'>('vector');
  const [selectedDataModelId, setSelectedDataModelId] = useState('');
  const [dataModels, setDataModels] = useState<DataModelCatalogRow[]>([]);
  const [dataModelsLoading, setDataModelsLoading] = useState(false);

  const [roleAccessList, setRoleAccessList] = useState<string[]>([]);
  const [userAccessList, setUserAccessList] = useState<string[]>([]);
  const [deniedUsers, setDeniedUsers] = useState<string[]>([]);
  const [allowedDepartmentIds, setAllowedDepartmentIds] = useState<string[]>([]);

  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState('');

  const [roleOptions, setRoleOptions] = useState<{ label: string; value: string }[]>([]);
  const [userOptions, setUserOptions] = useState<{ label: string; value: string }[]>([]);
  const [documents, setDocuments] = useState<{ fileName: string; filePath: string }[]>([]);
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [entityOptions, setEntityOptions] = useState<{ label: string; value: string }[]>([]);
  

  useEffect(() => {
    const loadEntities = async () => {
      try {
        const response = await ingestionService.getKBWentities(workspaceid);
        const options = response.map((ds: any) => ({
          label: ds.connectionName,
          value: ds.id
        }));
        setEntityOptions(options);
      } catch (err) {
        console.error('Failed to load data sources', err);
      }
    };
    loadEntities();
  }, [workspaceid]);

  useEffect(() => {
    if (destinationType !== 'vault') return;
    let cancelled = false;
    const loadDataModels = async () => {
      setDataModelsLoading(true);
      try {
        const list = await dataModelsCatalogService.list();
        if (!cancelled) {
          setDataModels(
            list.filter((dm) => {
              const status = (dm.status || '').toLowerCase();
              return !status || status === 'active';
            })
          );
        }
      } catch (err) {
        console.error('Failed to load data models', err);
        if (!cancelled) setDataModels([]);
      } finally {
        if (!cancelled) setDataModelsLoading(false);
      }
    };
    loadDataModels();
    return () => { cancelled = true; };
  }, [destinationType]);

  useEffect(() => {
    const fetchRolesAndUsers = async () => {
      setLoading(true);
      try {
        const roles = await authservice.getRoles();
        const users = await authservice.getUsers();
        setRoleOptions(roles.map((r: any) => ({ label: r.roleName, value: r.roleId })));
        setUserOptions(users.map((u: any) => ({ label: u.email, value: u.id })));
      } catch (err) {
        console.error('Failed to fetch roles or users', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchKbMeta = async () => {
      if (isEditMode && existingKbId) {
        setLoading(true);
        try {
          const meta = await apiService.get('/api/KnowledgeBase/GetSingleKnowledgeBaseMeta', {
            knowledgeBaseId: existingKbId,
            tenantId
          }, 'backend');
          // Response: id, title, description, tags, chunkingMode, roleAccessList, userAccessList, allowedDepartmentIds, deniedUsers, destinationType
          setTitle(meta.title ?? '');
          setDescription(meta.description ?? '');
          const apiChunking = meta.chunkingMode?.toLowerCase?.();
          setChunkingMode(
            apiChunking
              ? (chunkingOptions.find(opt =>
                  opt.replace(/\s/g, '').toLowerCase() === apiChunking
                ) ?? '')
              : ''
          );
          setTags(meta.tags ? (typeof meta.tags === 'string' ? meta.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : meta.tags) : []);
          setRoleAccessList(Array.isArray(meta.roleAccessList) ? meta.roleAccessList : []);
          setUserAccessList(Array.isArray(meta.userAccessList) ? meta.userAccessList : []);
          setDeniedUsers(Array.isArray(meta.deniedUsers) ? meta.deniedUsers : []);
          setAllowedDepartmentIds(Array.isArray(meta.allowedDepartmentIds) ? meta.allowedDepartmentIds : []);
          if (meta.destinationType === 'vault' || meta.destinationType === 'vector') {
            setDestinationType(meta.destinationType);
          }
          const modelId = meta.datamodel ?? meta.dataModel ?? meta.dataModelId ?? meta.DataModel ?? meta.DataModelId;
          if (modelId) {
            setSelectedDataModelId(String(modelId));
          }
        } catch (err) {
          console.error('Failed to fetch KB meta', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchRolesAndUsers();
    fetchKbMeta();
  }, [isEditMode, existingKbId, tenantId]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const uploaded: { fileName: string; filePath: string }[] = [];
    setUploading(true);
    setLoaderMessage("Uploading files...");
    for (const file of files) {
      const formData = new FormData();
      formData.append('tenantId', tenantId);
      formData.append('file', file);
      try {
        const res = await apiService.postForm('api/Upload/file', formData, 'backend');
        uploaded.push({ fileName: file.name, filePath: res.fullPath });
      } catch (err) {
        console.error('Upload failed for', file.name, err);
      }
    }
    setDocuments(prev => [...prev, ...uploaded]);
    setUploading(false);
    setLoaderMessage('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setLoaderMessage(isEditMode ? 'Updating...' : 'Saving...');
    try {
      if (isEditMode && existingKbId) {
        const payload = {
          knowledgeBaseId: existingKbId,
          tenantId,
          title,
          description,
          roleAccessList,
          deniedUsers,
          tags: tags.join(','),
          userAccessList,
          ...(allowedDepartmentIds.length ? { allowedDepartmentIds } : {})
        };
        const result = await knowledgebaseService.updateKnowledgeBase(payload);
        if (result) {
          onSubmit();
          onClose();
        }
      } else {
        if (destinationType === 'vault' && !selectedDataModelId) {
          setLoading(false);
          setLoaderMessage('');
          return;
        }
        const payload = {
          tenantID: tenantId,
          userID: userId,
          title,
          knowledgeWorkspaceID: workspaceid,
          description,
          tags: tags.join(','),
          roleAccessList,
          userAccessList,
          allowedDepartmentIds,
          deniedUsers,
          destinationType,
          ...(destinationType === 'vault'
            ? { datamodel: selectedDataModelId }
            : { chunkingMode: chunkingModeToApi(chunkingMode) || 'ByParagraph' })
        };
        const response = await knowledgebaseService.createKnowledgeBase(payload);
        if (response?.status === false) {
          alert(response.message || 'Failed to create knowledge base.');
        } else {
          onSubmit();
          onClose();
        }
      }
    } catch (err) {
      console.error('Submit failed:', err);
      alert('An unexpected error occurred.');
    } finally {
      setLoading(false);
      setLoaderMessage('');
    }
  };

  const addTag = () => {
    const trimmed = newTag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setNewTag('');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 text-[11px]">
      <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-neutral-900 rounded-xl shadow-xl border border-neutral-800 text-white">
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-800">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-white">
            <UploadCloud className="w-5 h-5 text-indigo-400" />
            {isEditMode ? 'Edit Knowledge Base' : 'Define Knowledge Base'}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-6">
          <div>
            <label className="block text-gray-400 mb-1">Title</label>
            <input
              placeholder="Knowledge Base title..."
              className="w-full bg-neutral-800 px-3 py-2 rounded border border-neutral-700 outline-none focus:ring-1 focus:ring-indigo-500"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-gray-400 mb-1">Description</label>
            <input
              placeholder="..."
              className="w-full bg-neutral-800 px-3 py-2 rounded border border-neutral-700 outline-none focus:ring-1 focus:ring-indigo-500"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* {!isEditMode && (
            <div>
              <label className="block text-gray-400 mb-1">Upload Documents</label>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="text-white bg-neutral-800 file:bg-neutral-700 file:text-white file:px-3 file:py-1 file:border-0 rounded cursor-pointer text-[11px]"
              />
              {uploading && <p className="mt-1 text-yellow-400">Uploading...</p>}
              {!uploading && documents.length > 0 && (
                <div className="mt-2 text-green-400 space-y-1">
                  <p>{documents.length} file{documents.length > 1 ? 's' : ''} uploaded:</p>
                  <ul className="list-disc list-inside text-[11px] text-green-300">
                    {documents.map((doc, index) => (
                      <li key={index}>{doc.fileName}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )} */}

          <FilterableMultiSelect label="Role Access" options={roleOptions} selected={roleAccessList} setSelected={setRoleAccessList} />
          <FilterableMultiSelect label="User Access" options={userOptions} selected={userAccessList} setSelected={setUserAccessList} />
          <FilterableMultiSelect label="Denied Users" options={userOptions} selected={deniedUsers} setSelected={setDeniedUsers} />
          <FilterableMultiSelect label="Allowed Departments (optional)" options={[]} selected={allowedDepartmentIds} setSelected={setAllowedDepartmentIds} />

          <div>
            <label className="block text-gray-400 mb-1">Destination type</label>
            <div className={`flex gap-4 mt-1 ${isEditMode ? 'opacity-80 cursor-not-allowed' : ''}`}>
              <label className={`flex items-center gap-2 ${isEditMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="radio"
                  name="destinationType"
                  checked={destinationType === 'vector'}
                  onChange={() => setDestinationType('vector')}
                  className="text-indigo-500 focus:ring-indigo-500"
                  disabled={isEditMode}
                />
                <Database className="w-4 h-4 text-indigo-400" aria-hidden />
                <span>Vector</span>
              </label>
              <label className={`flex items-center gap-2 ${isEditMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="radio"
                  name="destinationType"
                  checked={destinationType === 'vault'}
                  onChange={() => setDestinationType('vault')}
                  className="text-indigo-500 focus:ring-indigo-500"
                  disabled={isEditMode}
                />
                <Archive className="w-4 h-4 text-amber-400" aria-hidden />
                <span>Vault</span>
              </label>
            </div>
          </div>

          {destinationType === 'vault' && (
            <div>
              <label className="block text-gray-400 mb-1">Data Model</label>
              <select
                className="w-full bg-neutral-800 px-3 py-2 rounded border border-neutral-700 outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-80"
                value={selectedDataModelId}
                onChange={(e) => setSelectedDataModelId(e.target.value)}
                disabled={isEditMode || dataModelsLoading}
              >
                <option value="">
                  {dataModelsLoading ? 'Loading...' : 'Select data model...'}
                </option>
                {dataModels.map((dm) => (
                  <option key={dm.id} value={dm.id}>
                    {dm.modelName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {destinationType !== 'vault' && (
            <div>
              <label className="block text-gray-400 mb-1">Chunking Mode</label>
              <select
                className="w-full bg-neutral-800 px-3 py-2 rounded border border-neutral-700 outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-80"
                value={chunkingMode}
                onChange={(e) => setChunkingMode(e.target.value)}
                disabled={isEditMode}
              >
                <option value="">Select...</option>
                {chunkingOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-gray-400 mb-1">Tags</label>
            <div className="flex flex-wrap items-center gap-2">
              {tags.map(tag => (
                <span key={tag} className="bg-neutral-700 px-2 py-1 rounded-full">{tag}</span>
              ))}
              <input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Add tag..."
                className="bg-neutral-800 px-2 py-1 rounded border border-neutral-700 outline-none"
              />
              <button onClick={addTag} className="text-indigo-400">Add</button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-800 bg-neutral-950">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm">Cancel</button>
          <button
            onClick={handleSubmit}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-md text-sm shadow"
          >
            {isEditMode ? 'Update' : 'Submit'}
          </button>
        </div>
      </div>

      {(uploading || loading) && <LoaderOverlay message={loaderMessage} />}
    </div>
  );
};

export default UploadToKnowledgebaseModal;
